import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  ExternalLink,
  Building2,
  Users,
  Star,
  Eye,
  TrendingUp,
  Shield,
  Globe,
  Tag,
  Target,
  Zap,
  Info,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useEvent, useEventAccounts } from "@/hooks/useEvents";
import type { CarrRelevanceTier } from "@/hooks/useEvents";
import { format } from "date-fns";

// ============= HELPERS =============

const TIER_CONFIG: Record<string, { color: string; bg: string; icon: typeof Star; label: string }> = {
  "Tier 1: Must-Attend": { color: "text-red-700", bg: "bg-red-50 border-red-200", icon: Star, label: "Tier 1: Must-Attend" },
  "Tier 2: High-Value": { color: "text-amber-700", bg: "bg-amber-50 border-amber-200", icon: TrendingUp, label: "Tier 2: High-Value" },
  "Tier 3: Monitor": { color: "text-slate-600", bg: "bg-slate-50 border-slate-200", icon: Eye, label: "Tier 3: Monitor" },
};

function formatDateRange(start: string, end: string | null): string {
  const s = format(new Date(start + "T00:00:00"), "MMMM d, yyyy");
  if (!end || end === start) return s;
  const e = format(new Date(end + "T00:00:00"), "MMMM d, yyyy");
  return `${s} – ${e}`;
}

// ============= PAGE =============

const EventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data: event, isLoading } = useEvent(id || null);
  const { data: accounts = [] } = useEventAccounts(id || null);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Event not found</p>
        <Link to="/events">
          <Button variant="outline" className="mt-4">Back to Events</Button>
        </Link>
      </div>
    );
  }

  const tierCfg = event.carr_relevance_tier ? TIER_CONFIG[event.carr_relevance_tier] : null;

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Header */}
      <div className="border-b p-4">
        <Link to="/events" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
          <ArrowLeft className="h-4 w-4" />
          Back to Events
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">{event.event_name}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              {tierCfg && (
                <Badge variant="outline" className={`${tierCfg.bg} ${tierCfg.color} gap-1`}>
                  <tierCfg.icon className="h-3.5 w-3.5" />
                  {tierCfg.label}
                </Badge>
              )}
              {event.carr_event_category && (
                <Badge variant="secondary">{event.carr_event_category}</Badge>
              )}
              <Badge variant="outline" className="capitalize">{event.status}</Badge>
            </div>
          </div>
          {event.website_url && (
            <a href={event.website_url} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="gap-1.5">
                <ExternalLink className="h-4 w-4" />
                Website
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6 max-w-5xl">
        {/* Top row: Details + CARR Intelligence side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Event Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                Event Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                <span>{formatDateRange(event.start_date, event.end_date)}</span>
              </div>
              {event.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>{event.location}</span>
                </div>
              )}
              {event.venue && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>{event.venue}</span>
                </div>
              )}
              {event.organizer && (
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>Organized by {event.organizer}</span>
                </div>
              )}
              {event.estimated_attendance && (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>~{event.estimated_attendance.toLocaleString()} expected attendees</span>
                </div>
              )}

              {event.focus_areas && event.focus_areas.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Focus Areas</p>
                    <div className="flex flex-wrap gap-1.5">
                      {event.focus_areas.map((f, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-normal">{f}</Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {event.description && (
                <>
                  <Separator />
                  <p className="text-muted-foreground">{event.description}</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* CARR Intelligence */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" />
                CARR Intelligence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {event.carr_relevance_score != null && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Relevance Score</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          event.carr_relevance_score >= 80 ? "bg-red-500" :
                          event.carr_relevance_score >= 60 ? "bg-amber-500" : "bg-slate-400"
                        }`}
                        style={{ width: `${event.carr_relevance_score}%` }}
                      />
                    </div>
                    <span className="font-bold text-base">{event.carr_relevance_score}</span>
                  </div>
                </div>
              )}

              {event.strategic_notes && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Strategic Notes</p>
                  <p className="text-foreground">{event.strategic_notes}</p>
                </div>
              )}

              {event.recommended_action && (
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Recommended Action</p>
                    <p className="font-medium">{event.recommended_action}</p>
                  </div>
                </div>
              )}

              {event.territory && (
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Assigned Territory</p>
                    <p className="font-medium">{event.territory}</p>
                  </div>
                </div>
              )}

              {event.competitor_presence_likely && (
                <div className="flex items-center gap-2 text-orange-600 bg-orange-50 rounded-lg p-2.5">
                  <Shield className="h-4 w-4 shrink-0" />
                  <span className="font-medium text-sm">Competitor presence likely (Beckman, Thermo, Eppendorf)</span>
                </div>
              )}

              {event.target_buyer_personas && event.target_buyer_personas.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Target Buyer Personas</p>
                  <div className="flex flex-wrap gap-1.5">
                    {event.target_buyer_personas.map((p, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Linked Accounts */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Linked Accounts ({accounts.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {accounts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No accounts linked to this event yet</p>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground pb-2 border-b px-2">
                  <div className="col-span-4">Company</div>
                  <div className="col-span-3">Modalities</div>
                  <div className="col-span-2">Phase</div>
                  <div className="col-span-2">Territory</div>
                  <div className="col-span-1">Type</div>
                </div>
                {accounts.map((acct) => (
                  <Link
                    key={acct.id}
                    to={`/accounts/${acct.company_id}`}
                    className="grid grid-cols-12 gap-2 items-center text-sm py-2 px-2 rounded hover:bg-accent/50 transition-colors"
                  >
                    <div className="col-span-4 font-medium truncate">{acct.company_name || "Unknown"}</div>
                    <div className="col-span-3 flex gap-1 flex-wrap">
                      {acct.modalities?.slice(0, 2).map((m, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] font-normal">{m}</Badge>
                      ))}
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground capitalize">
                      {acct.clinical_phase?.replace(/_/g, " ") || "—"}
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground">{acct.territory || "—"}</div>
                    <div className="col-span-1">
                      <Badge variant="outline" className="text-[10px]">{acct.match_type}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team & Logistics */}
        {(event.team_members?.length || event.booth_number || event.notes) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Team & Logistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {event.team_members && event.team_members.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Team Members</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {event.team_members.map((m, i) => (
                      <Badge key={i} variant="secondary">{m}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {event.booth_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Booth Number</p>
                  <p className="font-medium">{event.booth_number}</p>
                </div>
              )}
              {event.notes && (
                <div>
                  <p className="text-xs text-muted-foreground">Notes</p>
                  <p className="text-muted-foreground">{event.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EventDetail;
